import XLSX from "xlsx-style"
import {saveAs} from "file-saver"

import Cell = XlsxStyle.Cell
import CellStyle = XlsxStyle.CellStyle
import Worksheet = XlsxStyle.Worksheet
import Workbook = XlsxStyle.Workbook
import {FileSaver} from "file-saver";

export const isCell = (object: any): object is Cell => (object !== null) && (typeof object === "object") && (("v" in object) || ("f" in object) || ("s" in object))

export const composeSheetData = (
    data: (number | boolean | string | Date | Cell)[][],
    formats: string[] = [],
    startRow: number = 0
): Worksheet => {
    let ws: Worksheet = {};
    let range: XlsxStyle.Range = {
        s: {
            c: 10000000,
            r: 10000000
        },
        e: {
            c: 0,
            r: 0
        }
    }

    for (let R = startRow; R !== data.length; ++R) {
        for (let C = 0; C !== data[R].length; ++C) {
            if (range.s.r > R) {
                range.s.r = R
            }
            if (range.s.c > C) {
                range.s.c = C
            }
            if (range.e.r < R) {
                range.e.r = R
            }
            if (range.e.c < C) {
                range.e.c = C
            }

            const value = data[R][C]

            if ((value === undefined) || (value === null)) {
                continue
            }

            let cell: XlsxStyle.Cell

            if (isCell(value)) {
                cell = value
            } else {
                cell = {
                    v: value,
                    s: {},
                }

                if (typeof value === "number") {
                    cell.t = "n"
                }  else if (typeof value === "boolean") {
                    cell.t = "b"
                } else {
                    cell.t = "s"
                }

                (<CellStyle>cell.s).numFmt = formats[C] || (<CellStyle>cell.s).numFmt
            }

            if (cell.v === undefined) {
                if (cell.f === undefined) {
                    cell.v = ""
                    cell.t = "s"
                }
            } else if (cell.v instanceof Date) {
                cell.t = "n";
                (<CellStyle>cell.s).numFmt = XLSX.SSF._table[14]
                cell.v = (<number>cell.v.valueOf() - (new Date(1899, 11, 29, 23)).valueOf()) / (24 * 60 * 60 * 1000)
            }

            const cell_ref = XLSX.utils.encode_cell({c: C, r: R});

            ws[cell_ref] = cell;
        }
    }

    if (range.s.c < 10000000) {
        ws["!ref"] = XLSX.utils.encode_range(range)
    }

    return ws;
}

function s2ab(s) {
    const buf = new ArrayBuffer(s.length)
    let view = new Uint8Array(buf)

    for (let i = 0; i !== s.length; ++i) {
        view[i] = s.charCodeAt(i) & 0xFF
    }

    return buf;
}

export const createWorkbook = (): Workbook => ({
    SheetNames: [],
    Sheets: {},
})

export const addWorksheet = (wb: Workbook, ws: Worksheet, sheetName: string): Workbook => {
    wb.SheetNames.push(sheetName)
    wb.Sheets[sheetName] = ws

    return wb
}

export const composeXlsx = (wb: XlsxStyle.Workbook): JSZip => {
    const zip = new JSZip()

    zip.load(XLSX.write(wb, {bookType: "xlsx", bookSST: true, type: "binary"}))

    return zip
}

export const addFilter = (zip: JSZip, columns: number, {row: r, sheetIndex}: {row?: number, sheetIndex?: number} = {row: 0, sheetIndex: 0}): JSZip => {
    r = r || 0
    sheetIndex = sheetIndex || 0

    const fileName = `xl/worksheets/sheet${sheetIndex + 1}.xml`

    zip.file(fileName, zip.file(fileName).asText().replace("</sheetData>", `</sheetData><autoFilter ref="${XLSX.utils.encode_range({c: 0, r}, {c: columns - 1, r})}"/>`))

    return zip
}

export const getStream = (zip: JSZip, filename): FileSaver =>
    saveAs(new Blob([s2ab(zip.generate({type: "string"}))], {type: "application/octet-stream"}), `${filename}.xlsx`)

export type Filter = {
    columns: number
    row?: number
}

export type SheetData = {
    sheet: Worksheet
    title: string
    filter: Filter
}